// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Validator, ValidationErrorMessage, Translate } from 'ra-core'
import { minValue, maxValue, maxLength, minLength } from 'react-admin'
import { pattern } from './pattern'

export const isHexColor: TValidator<string> = color => {
  if (color === '') return

  const hexColorRegex = /^#([\da-f]{3}){1,2}$|^#([\da-f]{4}){1,2}$/i
  const isValidHex = hexColorRegex.test(color)
  if (isValidHex) return

  return 'validation.hexColor'
}

/**
 * validators based on JSONSchema <type> validation (value argument)
 * with a type similar to that of react-admin's validators (property name)
 */
export const validators = {
  minLength: (min: number) => (value: string) => minLength(min)(value ?? ''),
  maxLength: (max: number) => (value: string) => maxLength(max)(value ?? ''),
  pattern,
  minValue: (min: number) => (value: number) => minValue(min)(value ?? 0),
  maxValue: (max: number) => (value: number) => maxValue(max)(value ?? 0),
  multipleOf: (divisor: number) => (value: number) => {
    if (value % divisor === 0) return

    return {
      message: 'validation.multipleOf',
      args: {
        divisor,
      },
    }
  },
} satisfies Partial<Record<string, (input: never) => TValidator<never>>>

/**
 * A single field validation function.
 *
 * Type is Based on {@link Validator}, but omits async api since
 * it is reserved for full form validation.*/
export type TValidator<V = unknown> = (
  value: V,
  allValues?: unknown,
  props?: unknown,
) => TValidationError

/**
 * A single field validation error message.
 *
 * Type is Based on {@link ValidationErrorMessage}, but omits async api since
 * it is reserved for full form validation.
 *
 * Either # or #.message may be a translation key path (dot delimited).
 */
export type TValidationError =
  | {
      message: string
      /** Record of args for translation if the key has any interpolations */
      args: Record<string, unknown>
    }
  | string
  | null
  | undefined

export const validationErrorToString =
  (translate: Translate) =>
  (error: TValidationError | undefined | null): string | null => {
    if (error === null || error === undefined) return null

    if (typeof error === 'string') return translate(error)
    if (typeof error === 'object' && error.message === 'string')
      return translate(error.message, error.args)

    console.warn("Validation error didn't match the expected shape", error)
    return translate('validation.invalid')
  }
